import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import {
  X, CheckCircle, Trash2, Edit, ArrowLeft,
  LayoutDashboard, Info, Clock, MessageSquare,
  FileText, Link2, Upload, Flag, CheckSquare,
  LayoutGrid, File, Activity, Rocket, Presentation,
  ClipboardList, Cpu, Target, Trophy,
  Pencil, ChevronDown, Check, Loader2
} from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'react-hot-toast';
import KanbanBoard from '../components/board/KanbanBoard';
import { useApiAuth } from '../../../contexts/ApiAuthContext';
import { projectService, taskService } from '..';
import { userService } from '../../users';
import { projectBoardService } from '../api/boardApi';

import ProjectSpecifications from '../components/ProjectSpecifications';
import ProjectOverview from '../components/ProjectOverview';
import LaunchStrategy from '../components/LaunchStrategy';
import TimelineView from '../components/timeline/TimelineView';
import DocumentItem from '../components/files/DocumentItem';
import Comments from '../components/Comments';
import Presentable from '../components/files/Presentable';
import ProjectEditModal from '../components/ProjectEditModal';
import { BRAND_COLORS, NEUTRAL_COLORS, SYSTEM_COLORS } from '../../../constants/brandPalette';

// Safe function to format activity timestamps that handles invalid dates
const formatActivityDate = (timestamp) => {
  if (!timestamp) return 'Unknown date';
  
  try {
    // Handle Firebase Timestamp objects
    if (timestamp && typeof timestamp.toDate === 'function') {
      return format(timestamp.toDate(), 'MMM d, yyyy h:mm a');
    }
    
    // Handle ISO strings and other date formats
    const date = new Date(timestamp);
    
    // Check if date is valid before formatting
    if (isNaN(date.getTime())) {
      return 'Invalid date';
    }
    
    return format(date, 'MMM d, yyyy h:mm a');
  } catch (error) {
    console.error('Error formatting date:', error);
    return 'Invalid date';
  }
};

const TabButton = ({ active, icon: Icon, label, onClick }) => (
  <button
    onClick={onClick}
    className="flex items-center gap-1.5 border-b-2 px-3 py-2 text-sm transition-colors"
    style={{
      borderColor: active ? BRAND_COLORS.primary : 'transparent',
      color: active ? BRAND_COLORS.primary : SYSTEM_COLORS.textMuted,
    }}
  >
    <Icon size={16} />
    {label}
  </button>
);

// ─── Inline edit helpers ──────────────────────────────────────────────────────
const PROJECT_STATUS_OPTIONS = [
  { value: 'planning',   label: 'Planning'    },
  { value: 'active',     label: 'Active'      },
  { value: 'inProgress', label: 'In Progress' },
  { value: 'on_hold',    label: 'On Hold'     },
  { value: 'completed',  label: 'Completed'   },
  { value: 'cancelled',  label: 'Cancelled'   },
];

const STATUS_CLS = {
  inProgress: 'bg-blue-100 text-blue-800',
  completed:  'bg-green-100 text-green-800',
  active:     'bg-indigo-100 text-indigo-800',
  on_hold:    'bg-amber-100 text-amber-800',
  cancelled:  'bg-red-100 text-red-800',
};
const getStatusCls = (s) => STATUS_CLS[s] || 'bg-yellow-100 text-yellow-800';

// Click-to-edit text field — shows ✓/✗ buttons; never auto-saves on blur
const InlineEditText = ({ value, onSave, as: Tag = 'p', multiline = false, className = '', placeholder = '', editable = true }) => {
  const [editing, setEditing] = useState(false);
  const [draft,   setDraft]   = useState('');
  const [saving,  setSaving]  = useState(false);

  const start  = () => { setDraft(value || ''); setEditing(true); };
  const cancel = () => setEditing(false);
  const commit = async () => {
    const v = (draft || '').trim();
    if (v === (value || '').trim()) { setEditing(false); return; }
    setSaving(true);
    try { await onSave(v); } catch {}
    setSaving(false);
    setEditing(false);
  };

  if (editing) {
    const base = 'flex-1 bg-transparent border-b-2 border-[#37559e] outline-none';
    const inputEl = multiline
      ? <textarea value={draft} onChange={e => setDraft(e.target.value)}
          onKeyDown={e => { if (e.key === 'Escape') cancel(); if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); commit(); } }}
          autoFocus rows={2} disabled={saving}
          className={`${base} resize-none text-sm leading-relaxed w-full ${className}`} />
      : <input type="text" value={draft} onChange={e => setDraft(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') cancel(); }}
          autoFocus disabled={saving} className={`${base} ${className}`} />;

    return (
      <div className="flex items-start gap-1.5 w-full">
        {inputEl}
        <div className="flex items-center gap-0.5 flex-shrink-0 mt-0.5">
          <button type="button" onClick={commit} disabled={saving} title="Save"
            className="p-1 rounded bg-[#37559e] text-white hover:bg-[#2d4785] transition-colors disabled:opacity-50">
            {saving ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
          </button>
          <button type="button" onClick={cancel} disabled={saving} title="Cancel"
            className="p-1 rounded text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors">
            <X size={12} />
          </button>
        </div>
      </div>
    );
  }

  if (!editable) return <Tag className={className}>{value || <span className="italic opacity-40">{placeholder}</span>}</Tag>;

  return (
    <div className="group/edit flex items-start gap-1.5 w-full cursor-text" onClick={start}>
      <Tag className={`flex-1 min-w-0 ${className}`}>
        {value || <span className="italic opacity-40">{placeholder}</span>}
      </Tag>
      <Pencil size={12} className="mt-0.5 flex-shrink-0 opacity-0 group-hover/edit:opacity-50 hover:!opacity-100 text-gray-400 hover:text-[#37559e] transition-opacity" />
    </div>
  );
};

// Click-to-edit status — shows pending confirmation before patching
const InlineStatusSelect = ({ value, onSave, editable = true }) => {
  const [open,    setOpen]    = useState(false);
  const [pending, setPending] = useState(null); // { value, label } waiting for confirm
  const [saving,  setSaving]  = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);

  const handleSelect = (opt) => { setOpen(false); if (opt.value !== value) setPending(opt); };
  const confirmChange = async () => {
    if (!pending) return;
    setSaving(true);
    try { await onSave(pending.value); } catch {}
    setSaving(false);
    setPending(null);
  };

  const label = PROJECT_STATUS_OPTIONS.find(o => o.value === value)?.label || value;
  const cls   = getStatusCls(value);

  // Pending confirmation chip
  if (pending) {
    return (
      <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-amber-50 border border-amber-200 rounded-full text-sm font-medium">
        <span className="text-amber-800">→ <strong>{pending.label}</strong>?</span>
        <button type="button" onClick={confirmChange} disabled={saving} title="Confirm"
          className="p-0.5 rounded bg-[#37559e] text-white hover:bg-[#2d4785] transition-colors disabled:opacity-50">
          {saving ? <Loader2 size={11} className="animate-spin" /> : <Check size={11} />}
        </button>
        <button type="button" onClick={() => setPending(null)} disabled={saving} title="Cancel"
          className="p-0.5 rounded text-gray-400 hover:text-red-500 transition-colors">
          <X size={11} />
        </button>
      </div>
    );
  }

  if (!editable) return <span className={`px-3 py-1 rounded-full text-base font-semibold ${cls}`}>{label}</span>;

  return (
    <div ref={ref} className="relative inline-block">
      <button type="button" onClick={() => setOpen(v => !v)}
        className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-base font-semibold hover:opacity-80 transition-opacity ${cls}`}>
        {label}
        <ChevronDown size={13} />
      </button>
      {open && (
        <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-xl z-[70] py-1 min-w-[140px]">
          {PROJECT_STATUS_OPTIONS.map(o => (
            <button key={o.value} type="button" onClick={() => handleSelect(o)}
              className={`w-full text-left px-3 py-1.5 text-sm hover:bg-gray-50 transition-colors ${o.value === value ? 'font-semibold text-[#37559e]' : 'text-gray-700'}`}>
              {o.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// File Upload Modal Component
const FileUploadModal = ({ isOpen, onClose, projectId, onFileUploaded }) => {
  const { user } = useApiAuth();
  const [file, setFile] = useState(null);
  const [fileName, setFileName] = useState('');
  const [description, setDescription] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  
  // Clear state when modal is closed
  useEffect(() => {
    if (!isOpen) {
      setFile(null);
      setFileName('');
      setDescription('');
      setIsUploading(false);
      setUploadError(null);
    }
  }, [isOpen]);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      // Check file size (5MB limit)
      if (selectedFile.size > 5 * 1024 * 1024) {
        setUploadError('File size exceeds 5MB limit');
        setFile(null);
        return;
      }
      
      setFile(selectedFile);
      setFileName(selectedFile.name);
      setUploadError(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!file) {
      setUploadError('Please select a file to upload');
      return;
    }
    
    if (!fileName.trim()) {
      setUploadError('Please enter a file name');
      return;
    }
    
    try {
      setIsUploading(true);
      setUploadError(null);
      
      // Get user's full name from Firebase Auth or Firestore
      let uploaderName = '';
      
      // First try to get from Firebase Auth displayName
      if (user.displayName) {
        uploaderName = user.displayName;
      } 
      // If not available, try to get from Firestore user document
      else {
        try {
          const userDoc = await userService.getUserById(user.id || user.uid);
          if (userDoc && (userDoc.name || userDoc.fullName)) {
            uploaderName = userDoc.name || userDoc.fullName;
          } else {
            // Last resort: use first part of email (before @)
            uploaderName = user.email.split('@')[0];
          }
        } catch (error) {
          console.error('Error fetching user data:', error);
          uploaderName = user.email.split('@')[0];
        }
      }
      
      const documentData = {
        name: fileName,
        description: description,
        uploadedBy: uploaderName,
        uploadedById: user.id || user.uid,
      };
      
      const result = await projectBoardService.addProjectDocument(projectId, documentData, file);
      
      toast.success('File uploaded successfully');
      onFileUploaded(result);
      onClose();
    } catch (error) {
      console.error('Error uploading file:', error);
      setUploadError(error.message || 'Failed to upload file');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Upload File">
      <form onSubmit={handleSubmit} className="space-y-4">
        {uploadError && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative" role="alert">
            <strong className="font-bold">Error: </strong>
            <span className="block sm:inline">{uploadError}</span>
          </div>
        )}
        
        <div className="mb-4">
          <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="file">
            File (Max 5MB)
          </label>
          <input
            type="file"
            id="file"
            onChange={handleFileChange}
            className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
            disabled={isUploading}
          />
        </div>
        
        <div className="mb-4">
          <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="fileName">
            File Name
          </label>
          <input
            type="text"
            id="fileName"
            value={fileName}
            onChange={(e) => setFileName(e.target.value)}
            className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
            disabled={isUploading}
            required
          />
        </div>
        
        <div className="mb-4">
          <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="description">
            Description (Optional)
          </label>
          <textarea
            id="description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
            rows="3"
            disabled={isUploading}
          />
        </div>
        
        <div className="flex justify-end space-x-2">
          <button
            type="button"
            onClick={onClose}
            className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
            disabled={isUploading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline flex items-center"
            disabled={isUploading}
          >
            {isUploading ? (
              <>
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Uploading...
              </>
            ) : (
              'Upload File'
            )}
          </button>
        </div>
      </form>
    </Modal>
  );
};

// Link Modal Component
const LinkModal = ({ isOpen, onClose, projectId, onLinkAdded }) => {
  const { user } = useApiAuth();
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [description, setDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  
  // Clear state when modal is closed
  useEffect(() => {
    if (!isOpen) {
      setName('');
      setUrl('');
      setDescription('');
      setIsSubmitting(false);
      setError(null);
    }
  }, [isOpen]);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate inputs
    if (!name.trim()) {
      setError('Please enter a name for the link');
      return;
    }
    
    if (!url.trim()) {
      setError('Please enter a URL');
      return;
    }
    
    // Ensure URL has http:// or https:// prefix
    let formattedUrl = url.trim();
    if (!formattedUrl.startsWith('http://') && !formattedUrl.startsWith('https://')) {
      formattedUrl = 'https://' + formattedUrl;
    }
    
    try {
      setIsSubmitting(true);
      setError(null);
      
      // Get user's full name from Firebase Auth or Firestore
      let uploaderName = '';
      
      // First try to get from Firebase Auth displayName
      if (user.displayName) {
        uploaderName = user.displayName;
      } 
      // If not available, try to get from Firestore user document
      else {
        try {
          const userDoc = await userService.getUserById(user.id || user.uid);
          if (userDoc && (userDoc.name || userDoc.fullName)) {
            uploaderName = userDoc.name || userDoc.fullName;
          } else {
            // Last resort: use first part of email (before @)
            uploaderName = user.email.split('@')[0];
          }
        } catch (error) {
          console.error('Error fetching user data:', error);
          uploaderName = user.email.split('@')[0];
        }
      }
      
      const linkData = {
        name: name.trim(),
        description: description.trim(),
        url: formattedUrl,
        uploadedBy: uploaderName,
        uploadedById: user.id || user.uid,
        type: 'link',
      };
      
      const result = await projectBoardService.addProjectDocument(projectId, linkData);
      
      toast.success('Link added successfully');
      onLinkAdded(result);
      onClose();
    } catch (error) {
      console.error('Error adding link:', error);
      setError(error.message || 'Failed to add link');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Add Link">
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative" role="alert">
            <strong className="font-bold">Error: </strong>
            <span className="block sm:inline">{error}</span>
          </div>
        )}
        
        <div className="mb-4">
          <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="name">
            Link Name
          </label>
          <input
            type="text"
            id="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
            disabled={isSubmitting}
            required
          />
        </div>
        
        <div className="mb-4">
          <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="url">
            URL
          </label>
          <input
            type="text"
            id="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
            placeholder="https://example.com"
            disabled={isSubmitting}
            required
          />
          <p className="text-xs text-gray-500 mt-1">
            If you don't include http:// or https://, https:// will be added automatically.
          </p>
        </div>
        
        <div className="mb-4">
          <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="linkDescription">
            Description (Optional)
          </label>
          <textarea
            id="linkDescription"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
            rows="3"
            disabled={isSubmitting}
          />
        </div>
        
        <div className="flex justify-end space-x-2">
          <button
            type="button"
            onClick={onClose}
            className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline flex items-center"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Adding...
              </>
            ) : (
              'Add Link'
            )}
          </button>
        </div>
      </form>
    </Modal>
  );
};

// Modal Component
const Modal = ({ isOpen, onClose, title, children }) => {
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-full max-w-md p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold">{title}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={20} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
};

// Helper functions for activity display
const getActivityIcon = (type) => {
  const t = (type || '').toLowerCase();
  if (t.startsWith('task'))               return <LayoutDashboard size={16} className="text-blue-500" />;
  if (t.startsWith('milestone'))          return <Flag size={16} className="text-green-500" />;
  if (t.includes('file') || t.includes('document')) return <FileText size={16} className="text-purple-500" />;
  if (t.includes('link'))                 return <Link2 size={16} className="text-indigo-500" />;
  if (t.includes('evidence'))             return <CheckSquare size={16} className="text-orange-500" />;
  if (t.includes('comment'))              return <MessageSquare size={16} className="text-yellow-500" />;
  if (t.includes('status'))              return <CheckCircle size={16} className="text-green-500" />;
  if (t.includes('clear'))               return <Trash2 size={16} className="text-red-500" />;
  return <Clock size={16} className="text-gray-500" />;
};

const getActivityIconBackground = (type) => {
  const t = (type || '').toLowerCase();
  if (t.startsWith('task'))               return 'bg-blue-100';
  if (t.startsWith('milestone'))          return 'bg-green-100';
  if (t.includes('file') || t.includes('document')) return 'bg-purple-100';
  if (t.includes('link'))                 return 'bg-indigo-100';
  if (t.includes('evidence'))             return 'bg-orange-100';
  if (t.includes('comment'))              return 'bg-yellow-100';
  if (t.includes('status'))              return 'bg-green-100';
  if (t.includes('clear'))               return 'bg-red-100';
  return 'bg-gray-100';
};

const getActivityDescription = (activity) => {
  if (activity.description) return activity.description;
  const d = activity.details || {};
  switch ((activity.type || '').toUpperCase()) {
    case 'TASK_CREATED':    return `Task "${d.title || 'Untitled'}" was created`;
    case 'TASK_UPDATED':    return `Task "${d.title || 'Untitled'}" was updated`;
    case 'TASK_COMPLETED':  return `Task "${d.title || 'Untitled'}" was completed`;
    case 'TASK_DELETED':    return `Task "${d.title || 'Untitled'}" was deleted`;
    case 'PROJECT_CREATED': return `Project "${d.name || 'Untitled'}" was created`;
    case 'PROJECT_UPDATED': return `Project "${d.name || 'Untitled'}" was updated`;
    case 'MEMBER_ADDED':    return d.name ? `${d.name} joined the project` : 'A new member joined';
    case 'MEMBER_REMOVED':  return d.name ? `${d.name} was removed` : 'A member was removed';
    default: {
      const human = (activity.type || 'Activity')
        .replace(/[_-]/g, ' ')
        .replace(/\b\w/g, c => c.toUpperCase());
      return d.title ? `${human}: ${d.title}` : d.name ? `${human}: ${d.name}` : human;
    }
  }
};

const ProjectDetailsModal = ({ project: initialProject, isOpen, onClose, onEdit, onDelete, viewMode = 'modal' }) => {
  const { user, hasRole } = useApiAuth();
  const navigate = useNavigate();
  const isPageView = viewMode === 'page';
  const canEdit = hasRole('admin') || hasRole('leader') || hasRole('hod') || hasRole('supervisor');
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [project, setProject] = useState(null);
  const [milestones, setMilestones] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [activities, setActivities] = useState([]);
  const [isFileUploadModalOpen, setIsFileUploadModalOpen] = useState(false);
  const [isLinkModalOpen, setIsLinkModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const fileInputRef = useRef(null);
  const [team, setTeam] = useState([]);

  const normalizeTaskList = (payload) => {
    if (Array.isArray(payload)) {
      return payload;
    }

    if (Array.isArray(payload?.tasks)) {
      return payload.tasks;
    }

    return [];
  };

  // Load project data including milestones
  const loadProjectData = useCallback(async () => {
    if ((!isPageView && !isOpen) || !initialProject?.id) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const projectData = await projectService.getProjectById(initialProject.id);
      const resolvedProject = projectData || initialProject;
      setProject(resolvedProject);

      const tasksData = await taskService.getProjectTasks(initialProject.id);
      setTasks(normalizeTaskList(tasksData));

      const shouldFetchMilestones = !Array.isArray(resolvedProject?.milestones);
      const shouldFetchDocuments = !Array.isArray(resolvedProject?.documents);
      const shouldFetchActivities = !Array.isArray(resolvedProject?.activities);

      const fetchQueue = [];
      if (shouldFetchMilestones) {
        fetchQueue.push({ key: 'milestones', promise: projectBoardService.getProjectMilestones(initialProject.id) });
      }
      if (shouldFetchDocuments) {
        fetchQueue.push({ key: 'documents', promise: projectBoardService.getProjectDocuments(initialProject.id) });
      }
      if (shouldFetchActivities) {
        fetchQueue.push({ key: 'activities', promise: projectBoardService.getProjectActivities(initialProject.id) });
      }

      const settled = await Promise.allSettled(fetchQueue.map((item) => item.promise));
      const fetchedData = fetchQueue.reduce((acc, item, index) => {
        const result = settled[index];
        acc[item.key] = result?.status === 'fulfilled' && Array.isArray(result.value) ? result.value : [];
        return acc;
      }, {});

      const milestonesData = Array.isArray(resolvedProject?.milestones)
        ? resolvedProject.milestones.map(ms => ({ ...ms, dueDate: ms.due_date || ms.dueDate || null }))
        : fetchedData.milestones || [];

      const documentsData = Array.isArray(resolvedProject?.documents)
        ? resolvedProject.documents
        : fetchedData.documents || [];

      const activitiesData = Array.isArray(resolvedProject?.activities)
        ? resolvedProject.activities
        : fetchedData.activities || [];

      const teamData = Array.isArray(resolvedProject?.members)
        ? resolvedProject.members
        : Array.isArray(resolvedProject?.team)
        ? resolvedProject.team
        : [];

      setMilestones(milestonesData);
      setDocuments(documentsData);
      setActivities(activitiesData);
      setTeam(teamData);

      setProject((prevProject) => ({
        ...prevProject,
        documents: Array.isArray(prevProject.documents) ? prevProject.documents : documentsData,
        milestones: Array.isArray(prevProject.milestones) ? prevProject.milestones : milestonesData,
        activities: Array.isArray(prevProject.activities) ? prevProject.activities : activitiesData,
        members: Array.isArray(prevProject.members) ? prevProject.members : teamData,
      }));
    } catch (error) {
      console.error('Error loading project data:', error);
      setError('Failed to load project data');
    } finally {
      setLoading(false);
    }
  }, [isOpen, isPageView, initialProject]);

  useEffect(() => {
    loadProjectData();
  }, [loadProjectData]);

  // Handler for milestone updates
  const handleMilestoneUpdate = useCallback(async () => {
    try {
      const milestonesData = await projectBoardService.getProjectMilestones(project.id);
      setMilestones(milestonesData);
      await loadProjectData();
    } catch (error) {
      console.error('Error updating milestones:', error);
      toast.error('Failed to update milestones');
    }
  }, [project?.id, loadProjectData]);

  const refreshProjectData = async () => {
    await loadProjectData();
  };

  // Add tabConfig definition
  const tabConfig = {
    requirements: {
      title: 'Project Requirements',
      icon: ClipboardList,
      sections: {
        functional: 'Functional Requirements',
        nonFunctional: 'Non-Functional Requirements',
        businessRules: 'Business Rules',
        userStories: 'User Stories'
      }
    },
    technical: {
      title: 'Technical Specifications',
      icon: Cpu,
      sections: {
        stack: 'Technology Stack',
        architecture: 'Architecture Overview',
        integration: 'Integration Points',
        infrastructure: 'Infrastructure Requirements'
      }
    },
    scope: {
      title: 'Scope Definition',
      icon: Target,
      sections: {
        boundaries: 'Project Boundaries',
        deliverables: 'Deliverables',
        constraints: 'Constraints',
        dependencies: 'Dependencies'
      }
    },
    success: {
      title: 'Success Criteria',
      icon: Trophy,
      sections: {
        metrics: 'Success Metrics',
        kpis: 'Key Performance Indicators',
        acceptance: 'Acceptance Criteria',
        validation: 'Validation Methods'
      }
    },
    launch: {
      title: 'Launch Strategy',
      icon: Rocket,
      sections: {
        market: 'Market Launch',
        advertising: 'Advertising & Promotion',
        deployment: 'Deployment & Release'
      }
    }
  };

  const handleUpdateSpecifications = (updatedSpecs) => {
    setProject(prev => ({ ...prev, specifications: updatedSpecs }));
  };

  const handleUpdateLaunchStrategy = async (updatedStrategy) => {
    try {
      setProject(prev => ({ ...prev, launchStrategy: updatedStrategy }));
      await projectService.updateLaunchStrategy(project.id, updatedStrategy);
      await addActivity({ type: 'launch_strategy_updated', description: 'Updated launch strategy' });
      toast.success('Launch strategy updated successfully');
    } catch (error) {
      console.error('Error updating launch strategy:', error);
      toast.error('Failed to update launch strategy');
    }
  };

  if (!isPageView && !isOpen) return null;
  
  if (loading) {
    return (
      <div className={isPageView ? 'rounded-2xl border p-6 mx-auto max-w-4xl' : 'mx-auto my-10 w-full max-w-3xl p-6 rounded-2xl border'} style={{ backgroundColor: SYSTEM_COLORS.shellSurface, borderColor: SYSTEM_COLORS.borderSoft }}>
        <div className="flex items-center gap-3">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-slate-400" />
          <span className="text-base font-medium" style={{ color: SYSTEM_COLORS.textMuted }}>
            Loading project data...
          </span>
        </div>
      </div>
    );
  }
  
  if (error) {
    if (isPageView) {
      return (
        <div className="rounded-xl border p-4" style={{ borderColor: 'rgba(220, 38, 38, 0.2)', backgroundColor: 'rgba(220, 38, 38, 0.08)' }}>
          <h2 className="text-lg font-semibold text-red-600">Error</h2>
          <p className="mt-2" style={{ color: SYSTEM_COLORS.textMuted }}>{error}</p>
          <button
            onClick={() => {
              setError(null);
              setLoading(true);
              loadProjectData();
            }}
            className="mt-3 rounded-lg px-4 py-2 text-white"
            style={{ backgroundColor: BRAND_COLORS.primary }}
          >
            Retry
          </button>
        </div>
      );
    }

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-red-600">Error</h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <X size={20} />
            </button>
          </div>
          <p className="text-gray-700 mb-4">{error}</p>
          <div className="flex justify-end gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-600 hover:text-gray-700"
            >
              Close
            </button>
            <button
              onClick={() => {
                setError(null);
                setLoading(true);
                loadProjectData();
              }}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }
  
  if (!project) {
    return null;
  }

  // Legacy milestone-based progress calculation (keeping for reference)
  
  




  const addActivity = async (activity) => {
    try {
      const activityData = {
        ...activity,
        timestamp: new Date().toISOString(),
        user: {
          id: user?.id || user?.uid || 'unknown',
          name: user?.fullName || user?.name || user?.displayName || user?.email || 'Unknown User'
        }
      };
      
      const createdActivity = await projectBoardService.addProjectActivity(project.id, activityData);
      
      // Update activities list
      setActivities(prev => [createdActivity || activityData, ...prev]);
    } catch (error) {
      console.error('Error adding activity:', error);
    }
  };

  // Handle file upload
  const handleFileUpload = async (e) => {
    try {
      let selectedFiles;
      
      // Handle both direct file input changes and modal callbacks
      if (e?.target?.files) {
        // Direct file input change
        selectedFiles = e.target.files;
        
        if (!selectedFiles || selectedFiles.length === 0) {
          toast.error('No files selected');
          return;
        }

        for (let i = 0; i < selectedFiles.length; i++) {
          const file = selectedFiles[i];
          if (file.size > 5 * 1024 * 1024) {
            toast.error('File size exceeds 5MB limit');
            return;
          }

          const documentData = {
            name: file.name,
            description: '',
            uploadedBy: user?.fullName || user?.name || user?.displayName || user?.email || 'Unknown User',
            uploadedById: user?.id || user?.uid || 'unknown',
          };
          
          const result = await projectBoardService.addProjectDocument(project.id, documentData, file);
          
          // Update the documents list
          setDocuments(prev => [result, ...prev]);
          
          // Add activity
          await addActivity({
            type: 'file_uploaded',
            description: `Added file: ${file.name}`,
            documentId: result.id
          });
          
          // Show success message
          toast.success(`File "${file.name}" uploaded successfully`);
        }
      } else if (e) {
        setDocuments(prev => [e, ...prev]);
        await addActivity({
          type: e.type === 'link' ? 'link_added' : 'file_uploaded',
          description: e.type === 'link' ? `Added link: ${e.name}` : `Added file: ${e.name}`,
          documentId: e.id
        });
        toast.success(`${e.type === 'link' ? 'Link' : 'File'} "${e.name}" added successfully`);
      } else {
        toast.error('No valid file or link data provided');
        return;
      }
    } catch (error) {
      console.error('Error handling file upload:', error);
      toast.error('Failed to process files');
    }
  };

  // Handle document deletion — optimistic, no full page reload
  const handleDeleteDocument = async (documentId) => {
    const documentToDelete = documents.find(doc => doc.id === documentId);
    if (!documentToDelete) return;

    // Optimistic remove
    setDocuments(prev => prev.filter(doc => doc.id !== documentId));
    try {
      await projectBoardService.deleteProjectDocument(project.id, documentId);
      addActivity({
        type: 'document_deleted',
        description: `Deleted: ${documentToDelete.name}`,
      });
      toast.success('Document deleted');
    } catch (error) {
      // Rollback
      setDocuments(prev => [documentToDelete, ...prev]);
      toast.error('Failed to delete document');
    }
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: Info },
    { id: 'specifications', label: 'Specifications', icon: FileText },
    { id: 'board', label: 'Board', icon: LayoutGrid },
    { id: 'timeline', label: 'Timeline', icon: Clock },
    { id: 'files', label: 'Files', icon: File },
    { id: 'activity', label: 'Activity', icon: Activity },
    { id: 'launch-strategy', label: 'Launch Strategy', icon: Rocket },
    { id: 'comments', label: 'Comments', icon: MessageSquare },
    { id: 'presentable', label: 'Presentables', icon: Presentation }
  ];


  const modalContent = (
    <div className={isPageView ? 'w-full' : 'fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50'}>
      <div className={isPageView ? 'w-full rounded-2xl border shadow-sm' : 'flex h-[90vh] w-full max-w-7xl flex-col rounded-lg'} style={{ backgroundColor: SYSTEM_COLORS.shellSurface, borderColor: SYSTEM_COLORS.borderSoft }}>
        {/* Header */}
        <div className="flex flex-col gap-2 border-b p-4 sm:flex-row sm:items-start sm:justify-between sm:p-5" style={{ borderColor: SYSTEM_COLORS.borderSoft }}>
          <div className="flex-1 min-w-0 space-y-3">
            {/* Breadcrumb — page view only */}
            {isPageView && (
              <button
                type="button"
                onClick={() => navigate('/projects')}
                className="inline-flex items-center gap-1 text-xs font-medium transition-colors hover:opacity-70"
                style={{ color: BRAND_COLORS.primary }}
              >
                <ArrowLeft size={12} />
                Projects
              </button>
            )}
            {/* Name */}
            <InlineEditText
              value={project.name}
              as="h2"
              className="text-xl font-bold"
              placeholder="Project name"
              editable={canEdit}
              onSave={async (val) => {
                await projectService.updateProject(project.id, { name: val });
                setProject(p => ({ ...p, name: val }));
              }}
            />
            {/* Description */}
            <InlineEditText
              value={project.description}
              as="p"
              multiline
              className="text-sm"
              placeholder="Add a description…"
              editable={canEdit}
              onSave={async (val) => {
                await projectService.updateProject(project.id, { description: val });
                setProject(p => ({ ...p, description: val }));
              }}
            />
          </div>
          <div className="flex items-center gap-3 self-start flex-shrink-0">
            {/* Status badge — right side, larger */}
            <InlineStatusSelect
              value={project.status}
              editable={canEdit}
              onSave={async (val) => {
                await projectService.updateProject(project.id, { status: val });
                setProject(p => ({ ...p, status: val }));
                addActivity({ type: 'status', description: `Changed status to ${val}` });
              }}
            />
            {!isPageView && (
              <>
                <button onClick={() => onDelete(project)} className="p-1.5 hover:bg-red-100 rounded-lg text-red-500" title="Delete">
                  <Trash2 size={17} />
                </button>
                <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-500">
                  <X size={17} />
                </button>
              </>
            )}
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="border-b" style={{ borderColor: SYSTEM_COLORS.borderSoft }}>
          <div className="flex gap-2 overflow-x-auto px-3 sm:px-4">
            {tabs.map(tab => (
              <TabButton
                key={tab.id}
                active={activeTab === tab.id}
                icon={tab.icon}
                label={tab.label}
                onClick={() => setActiveTab(tab.id)}
              />
            ))}
          </div>
        </div>

        {/* Content Area */}
        <div className={isPageView ? 'flex-1 overflow-y-auto p-4 sm:p-6' : 'flex-1 overflow-y-auto p-6'} style={{ backgroundColor: SYSTEM_COLORS.shellBackground }}>
          {loading ? (
            <div className="flex justify-center items-center h-full">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
            </div>
          ) : error ? (
            <div className="text-red-600 p-4 bg-red-50 rounded-lg">
              {error}
            </div>
          ) : (
            <>
              {activeTab === 'overview' && (
                <ProjectOverview
                  project={project}
                  tasks={tasks}
                  team={team}
                  milestones={milestones}
                  onRefresh={refreshProjectData}
                  canEditTeam={canEdit}
                />
              )}

              {activeTab === 'specifications' && (
                <div className="space-y-4 rounded-2xl border p-3 sm:p-4" style={{ borderColor: SYSTEM_COLORS.borderSoft, backgroundColor: SYSTEM_COLORS.shellSurface }}>
                  <ProjectSpecifications
                    project={project}
                    onUpdate={handleUpdateSpecifications}
                    tabConfig={tabConfig}
                  />
                </div>
              )}


              {activeTab === 'board' && (
                <div className="h-full rounded-2xl border p-2 sm:p-3" style={{ borderColor: SYSTEM_COLORS.borderSoft, backgroundColor: SYSTEM_COLORS.shellSurface }}>
                  <KanbanBoard
                    projectId={project.id}
                    currentUser={user}
                    onMilestoneUpdate={handleMilestoneUpdate}
                  />
                </div>
              )}

              {activeTab === 'timeline' && (
                <div className="space-y-4 rounded-2xl border p-3 sm:p-4" style={{ borderColor: SYSTEM_COLORS.borderSoft, backgroundColor: SYSTEM_COLORS.shellSurface }}>
                  <TimelineView
                    project={project}
                    milestones={milestones}
                    onUpdate={handleMilestoneUpdate}
                  />
                </div>
              )}

              {activeTab === 'files' && (
                <div className="flex h-full flex-col rounded-2xl border" style={{ borderColor: SYSTEM_COLORS.borderSoft, backgroundColor: SYSTEM_COLORS.shellSurface }}>
                  <div className="flex flex-col gap-3 border-b p-3 sm:flex-row sm:items-center sm:justify-between" style={{ borderColor: SYSTEM_COLORS.borderSoft }}>
                    <div className="flex items-center gap-3">
                      <h3 className="text-lg font-semibold" style={{ color: NEUTRAL_COLORS.black }}>Project Files</h3>
                      <div className="flex items-center gap-2 text-sm" style={{ color: SYSTEM_COLORS.textMuted }}>
                        <span>{documents.length} files</span>
                        {documents.length > 0 && (
                          <>
                            <span>·</span>
                            <span>{Math.round(documents.reduce((acc, doc) => acc + (doc.fileSize || 0), 0) / 1024)} KB total</span>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setIsLinkModalOpen(true)}
                        className="flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-sm font-medium"
                        style={{ color: SYSTEM_COLORS.textMuted, borderColor: SYSTEM_COLORS.borderSoft, backgroundColor: SYSTEM_COLORS.shellSurface }}
                      >
                        <Link2 size={14} />
                        Add Link
                      </button>
                      <button
                        onClick={() => fileInputRef.current?.click()}
                        className="flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-sm font-medium text-white"
                        style={{ backgroundColor: BRAND_COLORS.primary }}
                      >
                        <Upload size={14} />
                        Upload Files
                      </button>
                      <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleFileUpload}
                        className="hidden"
                        multiple
                      />
                    </div>
                  </div>

                  <div className="flex-1 overflow-y-auto p-3">
                    {documents.length === 0 ? (
                      <div className="flex flex-col items-center justify-center rounded-lg py-6" style={{ backgroundColor: SYSTEM_COLORS.surfaceSubtle }}>
                        <FileText size={32} className="mb-1" style={{ color: SYSTEM_COLORS.textSoft }} />
                        <p className="text-sm" style={{ color: SYSTEM_COLORS.textMuted }}>No files uploaded yet</p>
                        <p className="mt-1 text-xs" style={{ color: SYSTEM_COLORS.textSoft }}>Upload files or add links to get started</p>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                        {documents.map((doc) => (
                          <DocumentItem
                            key={doc.id}
                            document={doc}
                            onDelete={() => handleDeleteDocument(doc.id)}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {activeTab === 'activity' && (
                <div className="space-y-4 rounded-2xl border p-3 sm:p-4" style={{ borderColor: SYSTEM_COLORS.borderSoft, backgroundColor: SYSTEM_COLORS.shellSurface }}>
                  <h3 className="text-lg font-semibold" style={{ color: NEUTRAL_COLORS.black }}>Activity Timeline</h3>
                  
                  {activities.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-8 bg-gray-50 rounded-lg">
                      <Clock size={48} className="text-gray-300 mb-2" />
                      <p className="text-gray-500">No activity recorded yet</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {activities.map((activity, index) => (
                        <div key={activity.id || index} className="flex items-start gap-4 rounded-xl border p-3" style={{ borderColor: SYSTEM_COLORS.borderSoft, backgroundColor: SYSTEM_COLORS.surfaceSubtle }}>
                          <div className={`rounded-full p-2 ${getActivityIconBackground(activity.type)}`}>
                            {getActivityIcon(activity.type)}
                          </div>
                          <div>
                            <p style={{ color: NEUTRAL_COLORS.black }}>{getActivityDescription(activity)}</p>
                            <p className="text-sm" style={{ color: SYSTEM_COLORS.textMuted }}>
                              {formatActivityDate(activity.timestamp)}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'launch-strategy' && (
                <LaunchStrategy
                  project={project}
                  onUpdate={handleUpdateLaunchStrategy}
                  onRefresh={refreshProjectData}
                />
              )}

              {activeTab === 'comments' && (
                <div className="space-y-4 rounded-2xl border p-3 sm:p-4" style={{ borderColor: SYSTEM_COLORS.borderSoft, backgroundColor: SYSTEM_COLORS.shellSurface }}>
                  <Comments projectId={project.id} />
                </div>
              )}

              {activeTab === 'presentable' && (
                <div className="space-y-4 rounded-2xl border p-3 sm:p-4" style={{ borderColor: SYSTEM_COLORS.borderSoft, backgroundColor: SYSTEM_COLORS.shellSurface }}>
                  <Presentable projectId={project.id} />
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Project Edit Modal */}
      <ProjectEditModal
        project={project}
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        onSaved={(updated) => {
          setProject(prev => ({ ...prev, ...updated }));
          setTeam(updated?.members || updated?.team || team);
          addActivity({ type: 'status', description: 'Updated project details' });
        }}
      />

      {/* File Upload Modal */}
      <FileUploadModal
        isOpen={isFileUploadModalOpen}
        onClose={() => setIsFileUploadModalOpen(false)}
        projectId={project?.id}
        onFileUploaded={(result) => handleFileUpload(result)}
      />
      
      {/* Link Modal */}
      <LinkModal
        isOpen={isLinkModalOpen}
        onClose={() => setIsLinkModalOpen(false)}
        projectId={project?.id}
        onLinkAdded={(result) => handleFileUpload(result)}
      />
    </div>
  );

  if (isPageView) {
    return modalContent;
  }

  // Use ReactDOM.createPortal to render the modal at the root level of the DOM
  return ReactDOM.createPortal(modalContent, document.body);
};

export default ProjectDetailsModal;
